import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { DEBOUNCE_DELAY } from '../constants/formKConstants';
import type { FormKData } from '../types/formK.types';
import { createFormKSchema } from '../types/formK.types';
import { getDefaultControllingOwnerValues } from '../utils/formK.utils';

/**
 * Default empty values for Form K
 * Used for form initialization and reset
 */
const getDefaultFormKValues = (
  defaultValues?: Partial<FormKData>
): FormKData => ({
  originOfAssets: [],
  originOfAssetsOther: '',
  controllingOwnerType: '',
  controllingOwners: [getDefaultControllingOwnerValues()],
  shareholdersOtherCompanies: '',
  fiduciaryHolding: '',
  ...defaultValues,
});

/**
 * Custom hook for managing Form K data, validation, and state
 * Handles form initialization, submission, change detection with debouncing
 */
export const useFormKData = (
  tForms: (key: string) => string,
  defaultValues?: Partial<FormKData>,
  onSubmit?: (data: FormKData) => void,
  onError?: (error: Error) => void,
  onChange?: (data: Partial<FormKData>) => void
) => {
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Track if this is the initial mount to avoid unnecessary resets
  const isInitialMount = useRef(true);

  // Create schema with translations
  const formKSchema = useMemo(() => createFormKSchema(tForms), [tForms]);

  // Initialize form with react-hook-form
  const form = useForm<FormKData>({
    resolver: zodResolver(formKSchema),
    mode: 'all', // Validate on all events (blur, change, submit, trigger)
    defaultValues: getDefaultFormKValues(defaultValues),
  });

  /**
   * Memoize the data cleared check to prevent unnecessary effect triggers
   * Only recalculate when actual field values change, not when object reference changes
   */
  const isDataCleared = useMemo(() => {
    return (
      (!defaultValues?.shareholdersOtherCompanies ||
        defaultValues.shareholdersOtherCompanies === '') &&
      (!defaultValues?.fiduciaryHolding ||
        defaultValues.fiduciaryHolding === '') &&
      (!defaultValues?.controllingOwnerType ||
        defaultValues.controllingOwnerType === '') &&
      (!defaultValues?.originOfAssets ||
        defaultValues.originOfAssets.length === 0)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    defaultValues?.shareholdersOtherCompanies,
    defaultValues?.fiduciaryHolding,
    defaultValues?.controllingOwnerType,
    defaultValues?.originOfAssets?.length,
  ]);

  /**
   * Effect to reset form when defaultValues changes (e.g., when form is hidden and data is cleared).
   * This ensures the form state is properly cleaned up when the tab becomes hidden and then visible again.
   */
  useEffect(() => {
    // Skip the initial mount - defaultValues are already applied
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only reset if data is actually cleared
    if (isDataCleared) {
      // Reset the form to default empty values
      form.reset(getDefaultFormKValues());
    }
  }, [isDataCleared, form]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (data: FormKData) => {
      setSubmitError(null);

      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500));

        onSubmit?.(data);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'An error occurred';
        setSubmitError(errorMessage);
        onError?.(error as Error);
      }
    },
    [onSubmit, onError]
  );

  // Memoize onChange callback to prevent unnecessary re-renders
  const handleFormChange = useCallback(
    (data: Partial<FormKData>) => {
      if (onChange) {
        onChange(data);
      }
    },
    [onChange]
  );

  // Debounced onChange effect for form persistence using form.watch() subscription
  // This approach eliminates the re-render caused by useWatch on every keystroke
  useEffect(() => {
    if (!onChange) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const subscription = form.watch(data => {
      // Clear any pending debounce
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Debounce the onChange callback
      timeoutId = setTimeout(() => {
        handleFormChange(data as Partial<FormKData>);
      }, DEBOUNCE_DELAY);
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [form, handleFormChange, onChange]);

  return {
    form,
    submitError,
    handleSubmit,
  };
};
